import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, Calendar, Clock, AlertOctagon, CheckCircle2, XCircle, Filter, Search,
  Settings2, Users as UsersIcon, Trash2, Pencil, X, Check, ChevronLeft, ChevronRight,
  Bell, BellOff, Repeat, Palette,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { fmtDate } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import DateField from "@/components/DateField";
import ConfirmDialog from "@/components/ConfirmDialog";

const REMINDER_KIND_OPTIONS = [
  { v: "at_event", label: "Saat event" },
  { v: "5m", label: "5 menit sebelum" }, { v: "10m", label: "10 menit sebelum" },
  { v: "15m", label: "15 menit sebelum" }, { v: "30m", label: "30 menit sebelum" },
  { v: "1h", label: "1 jam sebelum" }, { v: "2h", label: "2 jam sebelum" },
  { v: "1d", label: "1 hari sebelum" }, { v: "2d", label: "2 hari sebelum" },
  { v: "1w", label: "1 minggu sebelum" },
  { v: "custom", label: "Custom tanggal + jam" },
];

const REPEAT_OPTIONS = [
  { v: "none", label: "Tidak Pernah" }, { v: "daily", label: "Harian" },
  { v: "weekdays", label: "Hari Kerja" }, { v: "weekends", label: "Akhir Pekan" },
  { v: "weekly", label: "Mingguan" }, { v: "biweekly", label: "Dua Mingguan" },
  { v: "monthly", label: "Bulanan" }, { v: "quarterly", label: "Setiap 3 Bulan" },
  { v: "semiannual", label: "Setiap 6 Bulan" }, { v: "yearly", label: "Tahunan" },
  { v: "custom", label: "Custom (interval)" },
];

const TABS = [
  { id: "all",       label: "Semua",       icon: Filter },
  { id: "today",     label: "Hari Ini",    icon: Calendar },
  { id: "upcoming",  label: "Akan Datang", icon: Clock },
  { id: "overdue",   label: "Terlambat",   icon: AlertOctagon, tone: "rose" },
  { id: "done",      label: "Selesai",     icon: CheckCircle2, tone: "emerald" },
  { id: "cancelled", label: "Dibatalkan",  icon: XCircle,      tone: "slate" },
];

const emptyEvent = {
  title: "", category_id: "", pic_ids: [], event_date: "", has_time: false, event_time: "",
  timezone: "Asia/Jakarta", priority: "Normal", note: "", attachments: [], reference: null,
  first_reminder: null, second_reminder: null, repeat: "none", repeat_custom: null,
  repeat_end: { kind: "never" },
};

export default function RemindersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // 149-cs (PROMPT 16-R2A-R2): default "Semua" saat baru dibuka. Deep-link
  // /info-agenda?tab=reminder&status=overdue akan meng-set tab langsung ke
  // "Terlambat". Perubahan tab disinkronkan balik ke URL supaya refresh &
  // share-link konsisten dengan yang ditampilkan.
  const urlStatus = searchParams.get("status");
  const validStatus = ["all", "today", "upcoming", "overdue", "done", "cancelled"];
  const [tab, setTab] = useState(validStatus.includes(urlStatus) ? urlStatus : "all");
  useEffect(() => {
    const cur = searchParams.get("status");
    if (validStatus.includes(cur) && cur !== tab) setTab(cur);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const switchTab = (next) => {
    setTab(next);
    // Preserve query lain (tab=reminder, dst) — hanya update status
    const merged = new URLSearchParams(searchParams);
    merged.set("status", next);
    setSearchParams(merged, { replace: true });
  };
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterPic, setFilterPic] = useState("");
  const [filterPrio, setFilterPrio] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState({ open: false, event: null, occurrence: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_occurrences: true };
      if (filterCat) params.category_id = filterCat;
      if (filterPic) params.pic_id = filterPic;
      if (filterPrio) params.priority = filterPrio;
      if (q) params.q = q;
      const { data } = await api.get("/reminders/events", { params });
      setEvents(data.events || []);
      setOccurrences(data.occurrences || []);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Gagal memuat");
    } finally {
      setLoading(false);
    }
  }, [filterCat, filterPic, filterPrio, q]);

  const loadCategories = useCallback(async () => {
    try {
      await api.get("/reminders/bootstrap");
      const { data } = await api.get("/reminders/categories");
      setCategories(data);
    } catch { /* silent */ }
  }, []);
  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers((data || []).map((u) => ({ id: u.id, name: u.name, division: u.division })));
    } catch {
      // 149-av: fallback if user lacks user_management perm — pakai personnel list
      try {
        const { data } = await api.get("/personnel");
        setUsers((data || []).map((u) => ({ id: u.id, name: u.name, division: u.division })));
      } catch {}
    }
  }, []);

  useEffect(() => { loadCategories(); loadUsers(); }, [loadCategories, loadUsers]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && events.length) {
      const ev = events.find((e) => e.id === openId);
      if (ev) { setEditing(ev); setFormOpen(true); }
      else {
        // Deep-link menunjuk event yg sudah terhapus / di luar scope.
        toast.info("Item tidak ditemukan — mungkin sudah dihapus atau di luar akses Anda.", { id: "deeplink-not-found" });
      }
      // Clear param supaya refresh tidak trigger ulang.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, events]);

  const filtered = useMemo(() => {
    // 149-cs (PROMPT 16-R2A-R2): timezone Asia/Jakarta konsisten dgn widget &
    // computeReminderStatus. "Semua" = seluruh reminder AKTIF (exclude cancelled).
    const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());
    return occurrences.filter((o) => {
      const od = (o.occurrence_date || "").slice(0, 10);
      if (tab === "all")       return o.status !== "cancelled";
      if (tab === "today")     return o.status === "scheduled" && od === today;
      if (tab === "upcoming")  return o.status === "scheduled" && od > today;
      if (tab === "overdue")   return o.status === "scheduled" && od < today;
      if (tab === "done")      return o.status === "done";
      if (tab === "cancelled") return o.status === "cancelled";
      return true;
    });
  }, [occurrences, tab]);

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (occOrEv) => {
    const eid = occOrEv.event_id || occOrEv.id;
    const ev = events.find((e) => e.id === eid);
    if (ev) { setEditing(ev); setFormOpen(true); }
  };

  const handleCompleteOccurrence = async (occ, scope = "this") => {
    try {
      await api.post(`/reminders/events/${occ.event_id}/occurrence/complete`, {
        occurrence_date: occ.occurrence_date, scope,
      });
      toast.success("Event ditandai selesai");
      await load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const handleCancelOccurrence = async (scope = "this") => {
    const occ = confirmCancel.occurrence;
    if (!occ) return;
    try {
      await api.post(`/reminders/events/${occ.event_id}/occurrence/cancel`, {
        occurrence_date: occ.occurrence_date, scope,
      });
      toast.success("Event dibatalkan");
      setConfirmCancel({ open: false, event: null, occurrence: null });
      await load();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="reminders-page">
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-slate-500">Reminder &amp; Event Center</p>
          <h1 className="font-display text-3xl text-[#0B1D3A]">Kalender Event &amp; Pengingat</h1>
          <p className="text-sm text-slate-500 mt-1">Semua event, deadline, dan reminder dalam satu tempat. Zona waktu <b>Asia/Jakarta</b>.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" data-testid="btn-manage-categories" onClick={() => setCatManagerOpen(true)}
            className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 uppercase text-xs tracking-wider font-semibold">
            <Palette size={13} /> Kelola Kategori
          </button>
          <button type="button" data-testid="btn-new-event" onClick={openNew}
            className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#FF5A1F] text-white hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold">
            <Plus size={13} /> Event Baru
          </button>
        </div>
      </div>

      <div className="border-b border-slate-200 mb-4">
        <div className="flex items-center gap-1 flex-wrap">
          {TABS.map((t) => {
            const active = t.id === tab;
            // Count harus konsisten dgn `filtered` logic (JKT + status rules)
            const jktToday = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());
            const count = occurrences.filter((o) => {
              const od = (o.occurrence_date || "").slice(0, 10);
              if (t.id === "all")       return o.status !== "cancelled";
              if (t.id === "today")     return o.status === "scheduled" && od === jktToday;
              if (t.id === "upcoming")  return o.status === "scheduled" && od > jktToday;
              if (t.id === "overdue")   return o.status === "scheduled" && od < jktToday;
              if (t.id === "done")      return o.status === "done";
              if (t.id === "cancelled") return o.status === "cancelled";
              return false;
            }).length;
            return (
              <button key={t.id} type="button" data-testid={`reminders-tab-${t.id}`} onClick={() => switchTab(t.id)}
                className={`px-4 py-2.5 text-xs uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-2 ${
                  active ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"
                }`}
              >
                <t.icon size={13} /> {t.label}
                {count > 0 && <span className={`px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${active ? "bg-[#FF5A1F] text-white" : "bg-slate-200 text-slate-600"}`}>{count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-3 mb-3 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Cari judul / catatan…" data-testid="reminders-search"
            className="pl-8 pr-2 py-2 border border-slate-300 text-xs w-64 focus:outline-none focus:border-[#FF5A1F]" />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="reminders-filter-category"
          className="border border-slate-300 text-xs px-2 py-2 bg-white">
          <option value="">Semua Kategori</option>
          {categories.filter((c) => c.is_active).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={filterPic} onChange={(e) => setFilterPic(e.target.value)} data-testid="reminders-filter-pic"
          className="border border-slate-300 text-xs px-2 py-2 bg-white">
          <option value="">Semua PIC</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select value={filterPrio} onChange={(e) => setFilterPrio(e.target.value)} data-testid="reminders-filter-priority"
          className="border border-slate-300 text-xs px-2 py-2 bg-white">
          <option value="">Semua Prioritas</option>
          <option value="Normal">Normal</option>
          <option value="Urgent">Urgent</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-300">
            <tr className="text-left">
              <th className="px-3 py-2.5 overline text-slate-700">Kategori</th>
              <th className="px-3 py-2.5 overline text-slate-700 min-w-[260px]">Judul</th>
              <th className="px-3 py-2.5 overline text-slate-700">Tanggal</th>
              <th className="px-3 py-2.5 overline text-slate-700">Waktu</th>
              <th className="px-3 py-2.5 overline text-slate-700">PIC</th>
              <th className="px-3 py-2.5 overline text-slate-700">Prio</th>
              <th className="px-3 py-2.5 overline text-slate-700">Reminder</th>
              <th className="px-3 py-2.5 overline text-slate-700">Repeat</th>
              <th className="px-3 py-2.5 overline text-slate-700">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="reminders-table">
            {loading && <tr><td colSpan={9} className="text-center py-6 text-slate-400">Memuat…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12" data-testid={`reminders-empty-${tab}`}>
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <Calendar size={32} />
                  <div className="text-sm font-semibold text-slate-700">Belum ada event di kategori ini</div>
                  <div className="text-xs">Klik "Event Baru" untuk membuat pengingat pertama Anda</div>
                </div>
              </td></tr>
            )}
            {!loading && filtered.map((o) => {
              const ev = events.find((e) => e.id === o.event_id) || {};
              const cat = catMap[o.category_id];
              return (
                <tr key={`${o.event_id}-${o.occurrence_date}`} data-testid={`reminders-row-${o.event_id}-${o.occurrence_date}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${o.status === "cancelled" ? "opacity-60" : ""} ${tab === "overdue" ? "bg-rose-50/40" : ""}`}>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border" style={{ borderColor: cat?.color, color: cat?.color }}>
                      {cat?.name || "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-medium text-[#0B1D3A]">
                    <div className="truncate max-w-[380px]" title={o.title}>{o.title}</div>
                    {ev.note && <div className="text-[10px] text-slate-500 truncate max-w-[380px]" title={ev.note}>{ev.note}</div>}
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-700">{fmtDate(o.occurrence_date)}</td>
                  <td className="px-3 py-2.5 tabular-nums text-slate-700">{o.has_time ? o.event_time : <span className="text-[10px] italic text-slate-400">Sepanjang hari</span>}</td>
                  <td className="px-3 py-2.5 text-slate-700">
                    {(o.pic_ids || []).map((pid) => userMap[pid]?.name).filter(Boolean).slice(0, 2).join(", ") || <span className="text-slate-300">—</span>}
                    {(o.pic_ids || []).length > 2 && <span className="text-[10px] text-slate-500"> +{o.pic_ids.length - 2}</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${o.priority === "Urgent" ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-700"}`}>{o.priority}</span>
                  </td>
                  <td className="px-3 py-2.5 text-[10px] text-slate-600">
                    {ev.first_reminder || ev.second_reminder ? (
                      <div>
                        {ev.first_reminder && <div className="flex items-center gap-1"><Bell size={9} /> 1st: {REMINDER_KIND_OPTIONS.find(k => k.v === ev.first_reminder.kind)?.label || ev.first_reminder.kind}</div>}
                        {ev.second_reminder && <div className="flex items-center gap-1"><Bell size={9} /> 2nd: {REMINDER_KIND_OPTIONS.find(k => k.v === ev.second_reminder.kind)?.label || ev.second_reminder.kind}</div>}
                      </div>
                    ) : <span className="text-slate-300 italic"><BellOff size={9} className="inline" /> tidak ada</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[10px] text-slate-600">
                    {ev.repeat && ev.repeat !== "none" ? (
                      <span className="inline-flex items-center gap-1"><Repeat size={9} />{REPEAT_OPTIONS.find(r => r.v === ev.repeat)?.label}</span>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <button type="button" data-testid={`reminders-edit-${o.event_id}`} onClick={() => openEdit(o)}
                        className="p-1 border border-slate-300 text-slate-700 hover:bg-slate-50" title="Ubah">
                        <Pencil size={11} />
                      </button>
                      {o.status === "scheduled" && (
                        <>
                          <button type="button" data-testid={`reminders-complete-${o.event_id}`}
                            onClick={() => handleCompleteOccurrence(o, "this")}
                            className="p-1 border border-emerald-500 text-emerald-700 hover:bg-emerald-50" title="Tandai selesai">
                            <Check size={11} />
                          </button>
                          <button type="button" data-testid={`reminders-cancel-${o.event_id}`}
                            onClick={() => setConfirmCancel({ open: true, event: ev, occurrence: o })}
                            className="p-1 border border-rose-500 text-rose-700 hover:bg-rose-50" title="Batalkan">
                            <X size={11} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <EventFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        categories={categories.filter((c) => c.is_active)}
        users={users}
        onSaved={() => { setFormOpen(false); setEditing(null); load(); }}
      />
      <CategoryManagerDialog
        open={catManagerOpen}
        onOpenChange={setCatManagerOpen}
        onReload={loadCategories}
        categories={categories}
        currentUser={user}
      />
      {confirmCancel.open && (
        <RecurrenceScopeDialog
          title="Batalkan Event"
          hasRepeat={confirmCancel.event?.repeat && confirmCancel.event.repeat !== "none"}
          onClose={() => setConfirmCancel({ open: false, event: null, occurrence: null })}
          onScope={handleCancelOccurrence}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recurrence scope dialog — this | this_and_following | all
// ---------------------------------------------------------------------------
function RecurrenceScopeDialog({ title, hasRepeat, onClose, onScope }) {
  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-none max-w-md">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <button type="button" data-testid="scope-this" onClick={() => onScope("this")}
            className="w-full text-left px-3 py-2 border border-slate-300 hover:bg-slate-50">
            <div className="font-semibold text-sm">Kejadian ini saja</div>
            <div className="text-xs text-slate-500">Hanya tanggal yang dipilih</div>
          </button>
          {hasRepeat && (
            <>
              <button type="button" data-testid="scope-this-and-following" onClick={() => onScope("this_and_following")}
                className="w-full text-left px-3 py-2 border border-slate-300 hover:bg-slate-50">
                <div className="font-semibold text-sm">Ini dan berikutnya</div>
                <div className="text-xs text-slate-500">Kejadian ini + semua sesudahnya</div>
              </button>
              <button type="button" data-testid="scope-all" onClick={() => onScope("all")}
                className="w-full text-left px-3 py-2 border border-slate-300 hover:bg-slate-50">
                <div className="font-semibold text-sm">Semua kejadian</div>
                <div className="text-xs text-slate-500">Seluruh recurrence dibatalkan</div>
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Event Form Dialog
// ---------------------------------------------------------------------------
function EventFormDialog({ open, onOpenChange, initial, categories, users, onSaved }) {
  const [form, setForm] = useState(emptyEvent);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm(initial ? {
      ...emptyEvent, ...initial,
      pic_ids: initial.pic_ids || [],
      repeat_end: initial.repeat_end || { kind: "never" },
    } : emptyEvent);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setReminder = (which, kind) => {
    if (!kind) { set(which, null); return; }
    set(which, { kind, custom_date: form[which]?.custom_date || "", custom_time: form[which]?.custom_time || "" });
  };
  const setReminderField = (which, k, v) => set(which, { ...form[which], [k]: v });

  const togglePic = (uid) => {
    const cur = form.pic_ids || [];
    set("pic_ids", cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid]);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!form.category_id) { toast.error("Kategori wajib dipilih"); return; }
    if (!form.event_date) { toast.error("Tanggal event wajib diisi"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (initial?.id) {
        await api.patch(`/reminders/events/${initial.id}`, payload);
        toast.success("Event diperbarui");
      } else {
        await api.post("/reminders/events", payload);
        toast.success("Event dibuat");
      }
      onSaved();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="event-form-dialog">
        <DialogHeader><DialogTitle>{initial ? "Ubah Event" : "Event Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="overline text-[10px] text-slate-500">Judul <span className="text-rose-600">*</span></label>
            <input type="text" data-testid="event-title" value={form.title} onChange={(e) => set("title", e.target.value)}
              className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm focus:outline-none focus:border-[#FF5A1F]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-[10px] text-slate-500">Kategori <span className="text-rose-600">*</span></label>
              <select data-testid="event-category" value={form.category_id} onChange={(e) => set("category_id", e.target.value)}
                className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white">
                <option value="">Pilih kategori…</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-[10px] text-slate-500">Prioritas</label>
              <select data-testid="event-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)}
                className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white">
                <option value="Normal">Normal</option><option value="Urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="overline text-[10px] text-slate-500">Tanggal <span className="text-rose-600">*</span></label>
              <DateField value={form.event_date} onChange={(v) => set("event_date", v)} testid="event-date" />
            </div>
            <div className="col-span-2">
              <label className="overline text-[10px] text-slate-500">Jam</label>
              <div className="flex items-center gap-2 mt-1">
                <label className="inline-flex items-center gap-1.5 text-xs">
                  <input type="checkbox" data-testid="event-has-time" checked={form.has_time} onChange={(e) => set("has_time", e.target.checked)} />
                  Punya jam
                </label>
                {form.has_time && (
                  <input type="time" data-testid="event-time" value={form.event_time || ""} onChange={(e) => set("event_time", e.target.value)}
                    className="px-2 py-1.5 border border-slate-300 text-sm" />
                )}
                <span className="text-[10px] text-slate-500 italic">TZ: Asia/Jakarta</span>
              </div>
            </div>
          </div>
          <div>
            <label className="overline text-[10px] text-slate-500">PIC / Penerima (bisa lebih dari 1)</label>
            <div className="mt-1 border border-slate-200 max-h-32 overflow-y-auto p-1">
              {users.map((u) => (
                <label key={u.id} data-testid={`event-pic-${u.id}`} className="flex items-center gap-2 text-xs px-1.5 py-1 hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={(form.pic_ids || []).includes(u.id)} onChange={() => togglePic(u.id)} />
                  <span>{u.name}</span>
                  <span className="text-[9px] text-slate-500 ml-auto">{u.division}</span>
                </label>
              ))}
              {users.length === 0 && <div className="text-xs text-slate-400 p-2 italic">Tidak ada user tersedia</div>}
            </div>
          </div>
          <div>
            <label className="overline text-[10px] text-slate-500">Catatan</label>
            <textarea data-testid="event-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2}
              className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm focus:outline-none focus:border-[#FF5A1F]" />
          </div>

          <div className="border border-slate-200 p-3 space-y-2">
            <div className="overline text-[10px] text-slate-500">Reminder (opsional, bisa keduanya kosong)</div>
            <ReminderRow which="first_reminder" label="First Reminder" value={form.first_reminder} onKind={(k) => setReminder("first_reminder", k)} onField={(k, v) => setReminderField("first_reminder", k, v)} />
            <ReminderRow which="second_reminder" label="Second Reminder" value={form.second_reminder} onKind={(k) => setReminder("second_reminder", k)} onField={(k, v) => setReminderField("second_reminder", k, v)} />
          </div>

          <div className="border border-slate-200 p-3 space-y-2">
            <div className="overline text-[10px] text-slate-500">Pengulangan</div>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="event-repeat" value={form.repeat} onChange={(e) => set("repeat", e.target.value)}
                className="px-2 py-1.5 border border-slate-300 text-xs bg-white">
                {REPEAT_OPTIONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
              {form.repeat === "custom" && (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-600">Setiap</span>
                  <input type="number" min="1" value={form.repeat_custom?.interval || 1}
                    onChange={(e) => set("repeat_custom", { ...(form.repeat_custom || {}), interval: parseInt(e.target.value) || 1 })}
                    className="w-16 px-2 py-1.5 border border-slate-300 text-xs" />
                  <select value={form.repeat_custom?.unit || "day"}
                    onChange={(e) => set("repeat_custom", { ...(form.repeat_custom || {}), unit: e.target.value })}
                    className="px-2 py-1.5 border border-slate-300 text-xs bg-white">
                    <option value="day">hari</option><option value="week">minggu</option>
                    <option value="month">bulan</option><option value="year">tahun</option>
                  </select>
                </div>
              )}
            </div>
            {form.repeat !== "none" && (
              <div className="grid grid-cols-3 gap-2">
                <select data-testid="event-repeat-end" value={form.repeat_end?.kind || "never"} onChange={(e) => set("repeat_end", { kind: e.target.value })}
                  className="px-2 py-1.5 border border-slate-300 text-xs bg-white">
                  <option value="never">Tidak berakhir</option>
                  <option value="until">Sampai tanggal</option>
                  <option value="after_n">Setelah N kejadian</option>
                </select>
                {form.repeat_end?.kind === "until" && (
                  <DateField value={form.repeat_end.until_date || ""} onChange={(v) => set("repeat_end", { kind: "until", until_date: v })} testid="event-repeat-until" />
                )}
                {form.repeat_end?.kind === "after_n" && (
                  <input type="number" min="1" data-testid="event-repeat-count" value={form.repeat_end.count || ""} onChange={(e) => set("repeat_end", { kind: "after_n", count: parseInt(e.target.value) || 1 })}
                    className="px-2 py-1.5 border border-slate-300 text-xs" placeholder="Jumlah kejadian" />
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="px-3 py-1.5 border border-slate-300 text-xs uppercase tracking-wider text-slate-700 hover:bg-slate-50">Batal</button>
          <button type="button" data-testid="event-save" disabled={busy} onClick={save}
            className="px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#e04a13]">{busy ? "Menyimpan…" : "Simpan"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReminderRow({ which, label, value, onKind, onField }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-center">
      <span className="text-xs text-slate-700">{label}</span>
      <select data-testid={`${which}-kind`} value={value?.kind || ""} onChange={(e) => onKind(e.target.value)}
        className="px-2 py-1.5 border border-slate-300 text-xs bg-white">
        <option value="">Tidak ada</option>
        {REMINDER_KIND_OPTIONS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
      </select>
      {value?.kind === "custom" && (
        <div className="flex items-center gap-1">
          <DateField value={value.custom_date || ""} onChange={(v) => onField("custom_date", v)} testid={`${which}-custom-date`} />
          <input type="time" value={value.custom_time || ""} onChange={(e) => onField("custom_time", e.target.value)}
            className="px-1.5 py-1.5 border border-slate-300 text-xs" data-testid={`${which}-custom-time`} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Category Manager Dialog
// ---------------------------------------------------------------------------
function CategoryManagerDialog({ open, onOpenChange, onReload, categories, currentUser }) {
  const { canPerm } = useAccess();
  const [editing, setEditing] = useState(null);
  // 149-av: gunakan RBAC matrix (bukan hardcoded division check) — Manage Categories
  // adalah izin terpisah `reminder_categories.update` yg dapat diatur admin.
  const canManage = canPerm(currentUser, "reminder_categories", "update");

  const save = async (payload) => {
    try {
      if (editing?.id) await api.patch(`/reminders/categories/${editing.id}`, payload);
      else await api.post("/reminders/categories", payload);
      toast.success("Kategori tersimpan");
      setEditing(null);
      onReload();
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    }
  };
  const del = async (c) => {
    try {
      await api.delete(`/reminders/categories/${c.id}`);
      toast.success("Kategori dihapus");
      onReload();
    } catch (err) {
      const msg = formatDetail(err.response?.data?.detail) || "";
      if (msg.includes("hanya boleh diarsipkan")) {
        // fallback: archive
        try {
          await api.post(`/reminders/categories/${c.id}/archive`);
          toast.success("Kategori diarsipkan (sudah dipakai — tidak bisa dihapus)");
          onReload();
        } catch { toast.error(msg); }
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl" data-testid="category-manager-dialog">
        <DialogHeader><DialogTitle>Kelola Kategori Event</DialogTitle></DialogHeader>
        {!canManage && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">Hanya admin dengan izin Manage Categories yang bisa mengubah kategori.</div>}
        <div className="space-y-1 max-h-80 overflow-y-auto border border-slate-200">
          {categories.map((c) => (
            <div key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 border-b border-slate-100 last:border-b-0">
              <span className="w-3 h-3 border border-slate-300" style={{ backgroundColor: c.color }} />
              <span className="text-sm flex-1">{c.name}</span>
              {!c.is_active && <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-600 uppercase tracking-wider">Arsip</span>}
              <span className="text-[10px] text-slate-500 tabular-nums">{c.usage_count || 0} event</span>
              {canManage && (
                <>
                  <button type="button" onClick={() => setEditing(c)} data-testid={`category-edit-${c.id}`} className="p-1 text-slate-600 hover:text-[#FF5A1F]"><Pencil size={11} /></button>
                  <button type="button" onClick={() => del(c)} data-testid={`category-delete-${c.id}`} className="p-1 text-rose-600 hover:bg-rose-50"><Trash2 size={11} /></button>
                </>
              )}
            </div>
          ))}
        </div>
        {canManage && (
          <CategoryForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSave={save}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function CategoryForm({ initial, onCancel, onSave }) {
  const [f, setF] = useState({ name: "", color: "#0B1D3A", icon: "calendar", order: 0, is_active: true });
  useEffect(() => {
    setF((prev) => initial ? { ...prev, ...initial } : { name: "", color: "#0B1D3A", icon: "calendar", order: 0, is_active: true });
  }, [initial]);
  return (
    <div className="mt-3 border-t border-slate-200 pt-3 space-y-2" data-testid="category-form">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{initial ? "Ubah" : "Tambah"} Kategori</div>
      <div className="grid grid-cols-3 gap-2">
        <input type="text" placeholder="Nama" data-testid="category-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
          className="col-span-2 px-2 py-1.5 border border-slate-300 text-sm" />
        <input type="color" data-testid="category-color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })}
          className="w-full h-9 border border-slate-300 cursor-pointer" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <input type="number" placeholder="Urutan" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value) || 0 })}
          className="px-2 py-1.5 border border-slate-300 text-xs" />
        <label className="inline-flex items-center gap-1.5 text-xs px-2">
          <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} /> Aktif
        </label>
        <div className="flex gap-1">
          <button type="button" data-testid="category-save" onClick={() => onSave(f)}
            className="flex-1 px-2 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider">Simpan</button>
          {initial && <button type="button" onClick={onCancel} className="px-2 py-1.5 border border-slate-300 text-xs">Batal</button>}
        </div>
      </div>
    </div>
  );
}
